import type Luminary from '../';
import { assertObject } from '../validators';
import type { MixinBase, MergeConstructors, GetConstructorParameters } from '../types';
import type { Template } from './template/Template';
import type { Scene } from './scene/Scene';
import type { BaseAsset } from './asset/BaseAsset';
import type { SerializableMixinSettings } from './serializableMixin';

export type ParentMixinSettings = {
    /**
     * the parent object
     */
    parent?: Object;
} & Partial<SerializableMixinSettings>;

export interface ParentMixinInterface {
    new (): typeof ParentMixin;
    _classType: string;
    get parent(): unknown;
    get container(): BaseAsset;
    get slide(): Scene | null;
    get root(): Luminary;
    get template(): Template;
}

/**
 * This Mixin provides a `parent` if one is required.
 *
 * Some Mixins require access to a `parent`. If the class we are mixing new behaviour into does not already provide a
 * `parent`, this Mixin is will provide a read-only parent.
 * @deprecated Use TypedParentMixin
 */
type RawParent = {
    container: BaseAsset;
    slide: Scene | null;
    root: Luminary;
    template: Template;
};

export const ParentMixin = <TBase extends MixinBase>(Base: TBase) => {
    const Derived = class extends (Base as any) {
        _classType = 'ParentFlowMixin';

        #parent: RawParent | null = null;

        constructor(settings: ParentMixinSettings = {}) {
            // eslint-disable-next-line constructor-super
            super(settings);

            if (settings.hasOwnProperty('parent')) {
                assertObject(settings.parent!);
                this.#parent = settings.parent as RawParent;
            }
        }

        /**
         * Our parent object.
         */
        get parent() {
            return this.#parent;
        }

        get container() {
            return this.parent?.container;
        }

        get slide() {
            return this.parent?.slide;
        }

        get root() {
            return this.parent?.root;
        }

        get template(): Template {
            return (this.parent?.slide?.template || this.parent?.template)!;
        }
    };

    return Derived as unknown as MergeConstructors<
        new (settings?: ParentMixinSettings) => {
            // `extends (Base as any)` gives instances an implicit `[x: string]: any` index
            // signature; preserve it so the published surface stays compatible with web.
            [x: string]: any;
        } & {
            [K in keyof InstanceType<typeof Derived>]: InstanceType<typeof Derived>[K];
        },
        TBase
    >;
};

export const TypedParentMixin = <TBase>(Base: MixinBase<TBase>) => {
    const Derived = ParentMixin(Base as any);

    type ReturnType = ParentMixinInterface & TBase;

    return Derived as unknown as new (
        props: GetConstructorParameters<typeof Derived>,
    ) => ReturnType;
};
